/**
 * ApiDropdown Component
 * A dropdown component that fetches options from API using useDropdownApi hook
 * Supports both single and multi-select with searchable autocomplete
 */

import React from 'react';
import {
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Checkbox,
  Autocomplete,
  createFilterOptions,
} from '@mui/material';
import { DropdownFieldConfig } from './types';
import { useDropdownApi } from './hooks/useDropdownApi';

interface ApiDropdownProps {
  field: DropdownFieldConfig;
  value: string | string[];
  onChange: (value: string | string[]) => void;
  selectedProtocols?: string[]; // For filtering sites based on selected protocols
  commonElementStyle?: React.CSSProperties; // Optional common style for dropdown elements
  currentTabKey?: string; // Current tab key to determine which dropdown data to use
}

const ApiDropdown: React.FC<ApiDropdownProps> = ({
  field,
  value,
  onChange,
  selectedProtocols,
  commonElementStyle,
  currentTabKey,
}) => {
  // Get responseKey from field config
  const responseKey = field.apiConfig?.responseKey || null;

  // Get options from Redux store with optional protocol filtering
  const { options: apiOptions } = useDropdownApi(responseKey, selectedProtocols, currentTabKey);

  // Use API options if available, otherwise use static options
  const options = responseKey ? apiOptions : field.options || [];

  const isMultiple = field.multiple ?? false;
  const isSearchable = field.searchable ?? false;

  const isSiteDropdown = responseKey === 'sites';

  // Custom filter function for site dropdown - use "contains" instead of "starts with"
  const customFilterOptions = createFilterOptions({
    matchFrom: 'any', // This makes it search anywhere in the string (contains)
    stringify: (option: { value: string; label: string }) => option.label,
  });

  // Use Autocomplete for searchable dropdowns
  if (isSearchable && isMultiple) {
    const arrayValue = Array.isArray(value)
      ? value
      : ((value as string) || '').split(',').filter((v) => v);
    const selectedOptions = options.filter((opt) => arrayValue.includes(opt.value));

    return (
      <Autocomplete
        key={field.id}
        multiple
        size="small"
        options={options}
        getOptionLabel={(option) => option.label}
        value={selectedOptions}
        onChange={(_, newValue) => {
          const selectedValues = newValue.map((opt) => opt.value);
          onChange(selectedValues);
        }}
        filterOptions={isSiteDropdown ? customFilterOptions : undefined}
        style={commonElementStyle}
        renderInput={(params) => (
          <TextField
            {...params}
            label={field.label}
            placeholder={field.placeholder}
            sx={{
              '& .MuiInputBase-input': {
                fontSize: '14px',
              },
            }}
          />
        )}
        renderOption={(props, option, { selected }) => (
          <li {...props} style={{ fontSize: '13px' }}>
            <Checkbox checked={selected} style={{ marginRight: 8 }} size="small" />
            {option.label}
          </li>
        )}
        disableCloseOnSelect
        ListboxProps={{
          style: {
            maxHeight: '300px',
            fontSize: '13px',
          },
        }}
      />
    );
  }

  // Normalize value
  const normalizedValue = isMultiple
    ? Array.isArray(value)
      ? value
      : ((value as string) || '').split(',').filter((v) => v)
    : (value as string) || '';

  // Standard Select for non-searchable dropdowns
  return (
    <FormControl key={field.id} size="small" style={commonElementStyle}>
      <InputLabel sx={{ fontSize: '14px' }}>{field.label}</InputLabel>
      <Select
        multiple={isMultiple}
        value={normalizedValue}
        label={field.label}
        onChange={(e) => onChange(e.target.value)}
        renderValue={
          isMultiple
            ? (selected) => {
                const selectedArray = Array.isArray(selected) ? selected : [];
                return selectedArray
                  .map((val) => {
                    const option = options.find((opt) => opt.value === val);
                    return option ? option.label : val;
                  })
                  .join(', ');
              }
            : undefined
        }
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 300,
              width: 'fit-content',
              minWidth: '300px',
            },
          },
        }}
        sx={{
          fontSize: '14px',
          '& .MuiSelect-select': {
            fontSize: '14px',
          },
          '& .MuiInputBase-input': {
            fontSize: '14px',
          },
        }}
      >
        {options?.map((option) => (
          <MenuItem key={option.value} value={option.value} sx={{ fontSize: '13px' }}>
            {isMultiple && (
              <Checkbox
                size="small"
                checked={
                  Array.isArray(normalizedValue)
                    ? normalizedValue.indexOf(option.value) > -1
                    : false
                }
              />
            )}
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default ApiDropdown;
